import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const input = this.refs.quantityInput;
    if (!input) return;

    const container = input.closest?.('.tw-qty-validation-wrapper');
    const msgEl = container
      ? container.querySelector('[data-max-qty-text]')
      : null;

    const maxAttr = input.getAttribute?.('data-max-qty');
    const max = maxAttr
      ? parseInt(maxAttr, 10)
      : input.max
        ? parseInt(input.max, 10)
        : NaN;

    const tpl = window?.translations?.cart?.max_qty;
    const maxQtyMessage = tpl ? tpl.replace('-[X]-', String(max)) : '';

    if (Number.isNaN(max)) {
      input.stepUp();
      if (msgEl) msgEl.textContent = '';
      this.#onQuantityChange();
      return;
    }

    const current = Number(input.value) || 0;

    if (current >= max) {
      if (msgEl) msgEl.textContent = maxQtyMessage;
      return;
    }

    input.stepUp();
    const newVal = Number(input.value);

    if (newVal <= max) {
      if (msgEl) msgEl.textContent = '';
    } else {
      if (msgEl) msgEl.textContent = maxQtyMessage;
      input.value = String(max);
    }

    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const input = this.refs.quantityInput;
    const container = input?.closest?.('.tw-qty-validation-wrapper');
    const msgEl = container
      ? container.querySelector('[data-max-qty-text]')
      : null;

    if (msgEl) {
      msgEl.textContent = '';
    }

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !== quantityInput
    )
      return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const input = this.refs.quantityInput;
    const container = input?.closest?.('.tw-qty-validation-wrapper');
    const msgEl = container
      ? container.querySelector('[data-max-qty-text]')
      : null;
    const maxAttr = input?.getAttribute?.('data-max-qty');
    const max = maxAttr
      ? parseInt(maxAttr, 10)
      : input?.max
        ? parseInt(input.max, 10)
        : NaN;

    const messageTemplate = window?.translations?.cart?.max_qty;
    const maxQtyMessage = messageTemplate.replace('-[X]-', String(max));

    if (Number.isNaN(max)) {
      if (event.target instanceof HTMLInputElement) {
        input.value = event.target.value;
      }
      if (msgEl) msgEl.textContent = '';
      this.#onQuantityChange();
      return;
    }

    const intended =
      event.target instanceof HTMLInputElement
        ? Number(event.target.value)
        : Number(input.value);

    if (intended > max) {
      if (msgEl) msgEl.textContent = maxQtyMessage;
      input.value = String(max);
      this.#onQuantityChange();
      return;
    }

    if (msgEl) msgEl.textContent = '';

    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(quantityInput.dataset.cartLine),
      ),
    );
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;

    const val = Number(quantityInput.value);
    const minVal = quantityInput.min !== '' ? Number(quantityInput.min) : 0;
    const maxVal =
      quantityInput.max !== '' ? Number(quantityInput.max) : Infinity;

    let next = Number.isFinite(val)
      ? val
      : Number.isFinite(minVal)
        ? minVal
        : 1;

    if (Number.isFinite(minVal) && next < minVal) next = minVal;
    if (Number.isFinite(maxVal) && next > maxVal) next = maxVal;

    quantityInput.value = String(next);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />',
      );
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent,
  );
}
